import { DetailIconBadge, DetailWell } from '@/components/patterns/detail-page'
import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Typography } from '@/components/ui/typography'
import { useAppForm } from '@/lib/form'
import { seasonalScheduleCreateSchema } from '@/lib/services/seasonal-content/seasonal-content.schema'
import { CalendarPlus } from 'lucide-react'
import {
  createSeasonalScheduleValues,
  SeasonalScheduleDefaultsFields,
  SeasonalScheduleTagField,
  SeasonalScheduleTimingFields,
} from './SeasonalScheduleFields'

export function SeasonalScheduleCreateSheet({ open, onOpenChange, options, onAdd }) {
  const form = useAppForm({
    defaultValues: createSeasonalScheduleValues(),
    validators: {
      onSubmit: seasonalScheduleCreateSchema,
      onSubmitAsync: async ({ value, formApi }) => {
        onAdd(value)
        formApi.reset()
        onOpenChange(false)
      },
    },
  })

  const closeSheet = () => {
    form.reset()
    onOpenChange(false)
  }

  return (
    <Sheet
      open={open}
      onOpenChange={(isOpen) => {
        if (isOpen) {
          onOpenChange(true)
        } else {
          closeSheet()
        }
      }}
    >
      <SheetContent className="w-full p-0 sm:max-w-md">
        <form.AppForm>
          <form.Form className="flex min-h-0 flex-1 flex-col">
            <SheetHeader className="border-b border-border/40 p-6 pr-12">
              <div className="flex items-center gap-3">
                <DetailIconBadge icon={CalendarPlus} tone="brand" />
                <div className="min-w-0">
                  <SheetTitle>Create schedule</SheetTitle>
                  <SheetDescription>
                    Choose the timing and defaults, then review it before publishing.
                  </SheetDescription>
                </div>
              </div>
            </SheetHeader>

            <div className="pretty-scrollbar min-h-0 flex-1 space-y-6 overflow-y-auto p-6">
              <SeasonalScheduleTagField form={form} options={options} />
              <SeasonalScheduleTimingFields form={form} layout="sheet" />
              <SeasonalScheduleDefaultsFields form={form} layout="sheet" />

              <DetailWell tone="amethyst" className="p-4">
                <Typography variant="labelStrong">What happens next?</Typography>
                <Typography variant="supporting" className="mt-1 block">
                  The schedule will appear first in the list. Save changes to publish it.
                </Typography>
              </DetailWell>
            </div>

            <SheetFooter className="flex-row justify-end border-t border-border/40 p-4">
              <Button type="button" variant="outline" onClick={closeSheet}>
                Cancel
              </Button>
              <form.Subscribe selector={(state) => state.isSubmitting}>
                {(isSubmitting) => (
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? 'Adding…' : 'Add schedule'}
                  </Button>
                )}
              </form.Subscribe>
            </SheetFooter>
          </form.Form>
        </form.AppForm>
      </SheetContent>
    </Sheet>
  )
}
